/**
 * UNIDADE DE DISPUTA — o conceito que unifica os três formatos de licitação que
 * o HUB opera. Espelha `helpers/unidade-disputa.util.ts` do backend.
 *
 *   1. GRUPO/LOTE      — vários itens julgados pelo TOTAL do grupo
 *   2. ITEM SOLTO      — cada item julgado por si (dispensa, pregão por item)
 *   3. MISTO           — grupos e itens soltos na mesma licitação
 *
 * Em todos, a unidade é o que tem vencedor próprio. Tratar N unidades como uma
 * é a raiz de uma família inteira de erros já vistos: o HUB gravou vitória
 * inexistente no BLL 021/2026 (1º num lote, 2º na licitação), somou pisos de
 * grupos que não disputamos, e exibiu o melhor lance de um lote nos outros.
 *
 * ⚠️ `groupNumber` PRIMEIRO, `groupIdentificador` só como reserva. Dois casos
 * reais puxam em direções opostas e juntos fixam a regra:
 *  - BLL 021/2026: groupIdentificador colapsado ('1','1','1') contra
 *    groupNumber 1,2,3 → `groupIdentificador ?? groupNumber` via 1 unidade.
 *  - ComprasNet 90004/2026: groupIdentificador VAZIO nos itens 1-5 e preenchido
 *    ('2','3') nos demais → a chave composta separava o item 5 do próprio grupo
 *    e contava 4 unidades onde há 3.
 */

export interface ItemDeUnidade {
  itemNumber?: number | null;
  groupNumber?: number | string | null;
  groupIdentificador?: string | null;
  quantity?: number | null;
  limitValue?: number | null;
  lastBid?: number | null;
  bestBid?: number | null;
  description?: string | null;
  allProposals?: any[] | null;
  [k: string]: any;
}

export interface UnidadeDisputa {
  chave: string;
  /** Número do grupo, ou null quando é item solto. */
  grupo: string | number | null;
  /** Rótulo pronto: "Grupo 2" ou "Item 7". */
  rotulo: string;
  itens: ItemDeUnidade[];
  /** Nenhum piso > 0: não participamos desta unidade. */
  naoCotada: boolean;
  /** Σ piso × qtd — o mínimo que aceitamos ofertar nesta unidade. */
  piso: number | null;
  /** Σ meu lance × qtd. */
  meuTotal: number | null;
  /** Σ melhor lance × qtd. */
  melhorTotal: number | null;
  /** Posição da IPCOM nesta unidade, quando o ranking dela foi coletado. */
  posicao: number | null;
  totalConcorrentes: number | null;
  /**
   * Quando ESTA unidade encerra (ms epoch), ou null sem dado.
   *
   * Cada unidade tem contagem própria e prorroga sozinha — o portal mostra um
   * relógio por grupo, e um número único para todos mente nos dois sentidos.
   * Medido no PE 127/2026 IFC (25/09/2026): seis grupos em 01:34, 01:42, 01:50,
   * 01:54, 01:57 e 01:57, com o HUB anunciando 01:04 para o conjunto.
   *
   * ⚠️ Dentro da unidade vale o MAIOR fim, não o menor. Todos os itens dela
   * encerram juntos, mas em prorrogação o portal empurra o fim a cada lance e o
   * update não chega a todos no mesmo tick: o menor é um valor velho, e pegá-lo
   * adianta o relógio. É a mesma regra que o header já aplicava (COREN-PE
   * 22/2026, 20/08: HUB em 00:27 com o portal em 01:12).
   */
  fimContagem: number | null;
}

/**
 * PCP "Por Valor Global": o lote é disputado por UM valor (o total do lote),
 * não por item. O backend (`pcp-dispute.service`, applyToSession) espelha a
 * linha do lote da sala em TODOS os itens daquele `groupNumber` — `bestBid` =
 * melhor valor global do lote, `lastBid` = NOSSO lance global. Ou seja, o
 * número que está em cada item JÁ É o total do lote: não multiplica por
 * quantidade nem soma entre os itens do grupo.
 *
 * Caso real (08/09/2026, sessão 126, 2 lotes): somar 13.800 × 504 quantidades
 * dava "MELHOR R$ 6.955.200" no lote 2, e o header mostrava o global do lote 1
 * (75.900), que nem cotamos.
 *
 * Hoje toda disputa PCP nossa é "Por Valor Global" (o robô só faz esse modo)
 * → `portalOrigem === 'pcp'` basta. Se entrar PCP por-item, criar flag.
 */
export function ehPcpValorGlobal(session: any): boolean {
  if (session?.item?.portalOrigem === 'pcp') return true;
  const nome = String(session?.portal?.portalName || '').toLowerCase();
  return nome.includes('pcp') || nome.includes('portal de compras');
}

export interface OpcoesAgrupar {
  /** Valor global por unidade (PCP): lê bestBid/lastBid de UM item, sem × qtd. */
  valorGlobal?: boolean;
}

/** Valor global da unidade: o primeiro `campo` > 0 entre os itens (é o mesmo em todos). */
export function valorGlobalDaUnidade(itens: ItemDeUnidade[], campo: 'lastBid' | 'bestBid'): number | null {
  for (const i of itens || []) {
    const v = Number(i?.[campo]);
    if (isFinite(v) && v > 0) return v;
  }
  return null;
}

export function chaveUnidade(item: ItemDeUnidade): string {
  const g = item?.groupNumber ?? item?.groupIdentificador ?? null;
  return g == null || g === '' ? `item-${item?.itemNumber}` : `g-${g}`;
}

const somaPor = (itens: ItemDeUnidade[], campo: 'limitValue' | 'lastBid' | 'bestBid'): number | null => {
  let total = 0;
  let algum = false;
  for (const i of itens) {
    const v = Number(i?.[campo]);
    if (!isFinite(v) || v <= 0) continue;
    const q = Number(i?.quantity) || 1;
    total += v * q;
    algum = true;
  }
  return algum ? total : null;
};

/** Posição da IPCOM no ranking da unidade (o mesmo em todos os itens do grupo). */
function posicaoNaUnidade(itens: ItemDeUnidade[]): { posicao: number | null; total: number | null } {
  for (const i of itens) {
    const props = Array.isArray(i?.allProposals) ? i.allProposals : [];
    if (props.length === 0) continue;
    const validas = props.filter((p: any) => !p?.desclassificada);
    const idx = validas.findIndex((p: any) => p?.ehMinha);
    if (idx >= 0) return { posicao: idx + 1, total: validas.length };
    return { posicao: null, total: validas.length };
  }
  return { posicao: null, total: null };
}

export function agruparPorUnidade(itens: ItemDeUnidade[], opcoes: OpcoesAgrupar = {}): UnidadeDisputa[] {
  const { valorGlobal = false } = opcoes;
  const mapa = new Map<string, ItemDeUnidade[]>();
  const ordem: string[] = [];
  for (const it of itens || []) {
    // PCP global sem groupNumber = processo de lote ÚNICO: todos os itens são o
    // mesmo lote (um valor global só). Separar por item somaria o global N vezes.
    const semGrupo = (it?.groupNumber ?? it?.groupIdentificador ?? null) == null
      || (it?.groupNumber ?? it?.groupIdentificador) === '';
    const chave = valorGlobal && semGrupo ? 'g-lote-unico' : chaveUnidade(it);
    if (!mapa.has(chave)) {
      mapa.set(chave, []);
      ordem.push(chave);
    }
    mapa.get(chave)!.push(it);
  }
  return ordem.map((chave) => {
    const grupoItens = mapa.get(chave)!;
    const primeiro = grupoItens[0];
    const grupo = primeiro?.groupNumber ?? primeiro?.groupIdentificador ?? null;
    const { posicao, total } = posicaoNaUnidade(grupoItens);
    const rotulo = grupo != null && grupo !== ''
      ? `Grupo ${grupo}`
      : chave === 'g-lote-unico' ? 'Lote único' : `Item ${primeiro?.itemNumber}`;
    return {
      chave,
      grupo,
      rotulo,
      itens: grupoItens,
      naoCotada: !grupoItens.some((i) => Number(i?.limitValue) > 0),
      piso: somaPor(grupoItens, 'limitValue'),
      // Global (PCP): o valor de um item já é o total do lote. Piso segue Σ×qtd —
      // é o que o robô compara com o global antes de cobrir.
      meuTotal: valorGlobal ? valorGlobalDaUnidade(grupoItens, 'lastBid') : somaPor(grupoItens, 'lastBid'),
      melhorTotal: valorGlobal ? valorGlobalDaUnidade(grupoItens, 'bestBid') : somaPor(grupoItens, 'bestBid'),
      posicao,
      totalConcorrentes: total,
      fimContagem: fimDaUnidade(grupoItens),
    };
  });
}

/**
 * MAIOR `fimContagem` entre os itens da unidade — ver o campo na interface.
 * Aceita epoch em número e string de data; descarta o que não vira data.
 */
function fimDaUnidade(itens: ItemDeUnidade[]): number | null {
  let maior: number | null = null;
  for (const it of itens || []) {
    const bruto = it?.fimContagem;
    if (bruto == null || bruto === '') continue;
    const ts = typeof bruto === 'number' ? bruto : new Date(bruto).getTime();
    if (!Number.isFinite(ts)) continue;
    if (maior == null || ts > maior) maior = ts;
  }
  return maior;
}
